import { useEffect, useMemo, useState } from 'react';
import {
  getMessageDetails,
  getThreadState,
} from '../services/api';
import { normalizeContentBlocks } from '../utils/publishedImages';

const MAX_RESULTS = 200;
const MAX_PENDING = 64;

const detailResults = new Map();
const displayRefreshes = new Map();
// A 409 may replace a message's revision while a caller still holds the old
// detail reference. Redirect only that exact requested revision; an identity-
// wide fallback would incorrectly return r2 data for a later unseen r3.
const detailKeyRedirects = new Map();
const refreshedDisplayMessages = new Map();
const pendingRequests = new Map();
const detailErrors = new Map();
const listeners = new Set();
let cacheGeneration = 0;
let detailStoreVersion = 0;

const trimMap = (map, maxEntries) => {
  while (map.size > maxEntries) {
    map.delete(map.keys().next().value);
  }
};

const notify = () => {
  detailStoreVersion += 1;
  listeners.forEach((listener) => {
    try { listener(); } catch (_) {}
  });
};

// MessageList keeps row props stable for virtualization, so a row cannot rely
// on its parent re-rendering when this shared cache changes. Expose the store
// subscription separately so memoized rows can observe the same notifications
// without changing the controller object identity.
export const subscribeMessageDetails = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getMessageDetailsStoreVersion = () => detailStoreVersion;

export const getMessageId = (message) => {
  const value = message?.message_id ?? message?.id ?? message?.detail_ref?.message_id;
  return value == null || value === '' ? '' : String(value);
};

export const getMessageDetailRef = (message) => {
  const ref = message?.detail_ref;
  if (!ref || typeof ref !== 'object') return null;
  const messageId = ref.message_id == null || ref.message_id === '' ? '' : String(ref.message_id);
  const revision = typeof ref.revision === 'string' ? ref.revision : String(ref.revision ?? '');
  if (!messageId || !revision) return null;
  return { message_id: messageId, revision };
};

export const messageDetailKey = (threadUid, message) => {
  const ref = getMessageDetailRef(message);
  if (!threadUid || !ref) return '';
  return `${String(threadUid)}\u001f${ref.message_id}\u001f${ref.revision}`;
};

const messageIdentityKey = (threadUid, message) => {
  const id = getMessageId(message);
  return threadUid && id ? `${String(threadUid)}\u001f${id}` : '';
};

const refsMatch = (expected, actual) => (
  !!expected
  && !!actual
  && String(expected.message_id) === String(actual.message_id)
  && String(expected.revision) === String(actual.revision)
);

const normalizeDetailMessage = (message) => {
  const normalized = { ...message };
  if (!Array.isArray(normalized.content_blocks) && Array.isArray(normalized.contentBlocks)) {
    normalized.content_blocks = normalizeContentBlocks(normalized.contentBlocks);
  } else if (Array.isArray(normalized.content_blocks)) {
    normalized.content_blocks = normalizeContentBlocks(normalized.content_blocks);
  }
  if (!Array.isArray(normalized.functionLogs) && Array.isArray(normalized.function_logs)) {
    normalized.functionLogs = normalized.function_logs.map((entry) => ({ ...entry }));
  }
  if (!Array.isArray(normalized.streamParts) && Array.isArray(normalized.stream_parts)) {
    normalized.streamParts = normalized.stream_parts.map((part) => ({ ...part }));
  }
  if (!normalized.thinkingInsight && normalized.thinking_insight && typeof normalized.thinking_insight === 'object') {
    normalized.thinkingInsight = { ...normalized.thinking_insight };
  }
  return normalized;
};

const extractDetailMessage = (payload) => {
  const candidate = payload?.message && typeof payload.message === 'object'
    ? {
        ...payload.message,
        ...(payload.detail_ref && !payload.message.detail_ref ? { detail_ref: payload.detail_ref } : {}),
        ...(payload.details_loaded === true && payload.message.details_loaded == null ? { details_loaded: true } : {}),
      }
    : payload;
  if (!candidate || typeof candidate !== 'object') {
    throw new Error('Message details response was empty.');
  }
  return normalizeDetailMessage(candidate);
};

const createDetailError = (message, status = null) => {
  const error = new Error(message);
  if (status != null) error.status = status;
  return error;
};

const rejectedDetailRequest = (error) => {
  const request = Promise.reject(error);
  // Keep the synchronous controller API promise-shaped without creating an
  // unhandled rejection for UI callbacks that do not await early failures.
  request.catch(() => {});
  return request;
};

export const clearMessageDetailsCache = () => {
  cacheGeneration += 1;
  detailResults.clear();
  displayRefreshes.clear();
  detailKeyRedirects.clear();
  refreshedDisplayMessages.clear();
  detailErrors.clear();
  // Do not cancel fetches, but their completions cannot be observed by a
  // disposed cache because all result/pending entries are removed first.
  pendingRequests.clear();
  notify();
};

export const getCachedMessageDetails = (threadUid, message) => {
  const key = messageDetailKey(threadUid, message);
  return key ? detailResults.get(key) || null : null;
};

export const createMessageDetailsController = () => {
  const requestMessageDetails = (threadUid, message, options = {}) => {
    const ref = getMessageDetailRef(message);
    const key = messageDetailKey(threadUid, message);
    if (!threadUid || !ref || !key) {
      return rejectedDetailRequest(createDetailError('Message details are unavailable for this message.'));
    }

    const cached = detailResults.get(key);
    if (cached) return Promise.resolve(cached);
    if (pendingRequests.has(key)) return pendingRequests.get(key);
    if (pendingRequests.size >= MAX_PENDING) {
      return rejectedDetailRequest(createDetailError('Too many message detail requests are active. Try again shortly.'));
    }
    const requestGeneration = cacheGeneration;
    detailErrors.delete(key);

    const request = (async () => {
      try {
        const payload = await getMessageDetails(threadUid, ref.message_id, ref.revision, {
          signal: options.signal,
        });
        const rawDetails = extractDetailMessage(payload);
        // Detail responses replace the display object, but the display
        // provenance still belongs to the rendered message. Preserve it so
        // the display-only ordering/reconciliation guard remains stable while
        // the full payload is being rendered.
        const details = message.__henosis_display_projection === true
          ? {
              ...rawDetails,
              __henosis_display_projection: true,
              ...(message.__henosis_display_version != null
                ? { __henosis_display_version: message.__henosis_display_version }
                : {}),
            }
          : rawDetails;
        const returnedRef = getMessageDetailRef(details);
        if (!refsMatch(ref, returnedRef) || details.details_loaded !== true) {
          throw createDetailError('Message details response did not match the requested revision.');
        }
        if (requestGeneration !== cacheGeneration) {
          throw createDetailError('Message details request was invalidated by the active session.');
        }
        detailResults.set(key, details);
        detailErrors.delete(key);
        trimMap(detailResults, MAX_RESULTS);
        notify();
        return details;
      } catch (error) {
        // A revision conflict is recoverable, but never by retrying the old
        // fingerprint. Refresh only the lightweight display state, then retry
        // once with the newly returned reference.
        if (error?.status === 409 && options.allowRevisionRefresh !== false) {
          try {
            const identityKey = messageIdentityKey(threadUid, message);
            let refreshed = displayRefreshes.get(identityKey);
            if (!refreshed) {
              refreshed = getThreadState(threadUid, { view: 'display' })
                .then((state) => {
                  const freshMessage = Array.isArray(state?.messages)
                    ? state.messages.find((candidate) => getMessageId(candidate) === ref.message_id)
                    : null;
                  if (!freshMessage) throw error;
                  if (requestGeneration !== cacheGeneration) {
                    throw createDetailError('Display refresh was invalidated by the active session.');
                  }
                  refreshedDisplayMessages.set(identityKey, freshMessage);
                  trimMap(refreshedDisplayMessages, MAX_RESULTS);
                  notify();
                  return freshMessage;
                })
                .finally(() => displayRefreshes.delete(identityKey));
              displayRefreshes.set(identityKey, refreshed);
            }
            const freshMessage = await refreshed;
            const freshRef = getMessageDetailRef(freshMessage);
            if (freshRef && !refsMatch(ref, freshRef)) {
              const freshKey = messageDetailKey(threadUid, freshMessage);
              if (freshKey) {
                detailKeyRedirects.set(key, freshKey);
                refreshedDisplayMessages.set(key, freshMessage);
                trimMap(detailKeyRedirects, MAX_RESULTS);
                trimMap(refreshedDisplayMessages, MAX_RESULTS);
                notify();
              }
              return requestMessageDetails(threadUid, freshMessage, {
                ...options,
                allowRevisionRefresh: false,
              });
            }
          } catch (refreshError) {
            // Refresh failures must become visible on the original stale key;
            // otherwise the expanded row has no error and cannot offer retry.
            if (requestGeneration === cacheGeneration) {
              detailErrors.set(key, refreshError);
              notify();
            }
            throw refreshError;
          }
        }
        if (requestGeneration === cacheGeneration) {
          detailErrors.set(key, error);
          notify();
        }
        throw error;
      } finally {
        if (pendingRequests.get(key) === request) pendingRequests.delete(key);
        // The completion notification above publishes the result/error, but
        // the pending entry is still present at that point. Notify once more
        // after removal so consumers do not remain stuck in loading=true.
        if (requestGeneration === cacheGeneration) notify();
      }
    })();
    pendingRequests.set(key, request);
    // Detail loading is also started by button/effect callbacks that do not
    // await the returned promise. Mark this promise handled at the request
    // boundary so a 4xx/5xx remains available to callers that do await it,
    // while UI fire-and-forget paths cannot become unhandled rejections.
    request.catch(() => {});
    // Publish after the pending entry exists so the first render observes
    // loading=true rather than briefly seeing a cleared error and loading=false.
    notify();
    return request;
  };

  return {
    getMessage: (threadUid, message) => {
      const key = messageDetailKey(threadUid, message);
      const redirectedKey = key ? detailKeyRedirects.get(key) : '';
      if (redirectedKey && detailResults.has(redirectedKey)) {
        return detailResults.get(redirectedKey);
      }
      if (key && detailResults.has(key)) return detailResults.get(key);
      return (key && refreshedDisplayMessages.get(key)) || message;
    },
    getState: (threadUid, message) => {
      const key = messageDetailKey(threadUid, message);
      if (!key) return { loaded: false, loading: false, error: null };
      const resolvedKey = detailKeyRedirects.get(key) || key;
      const result = detailResults.get(resolvedKey);
      const pending = pendingRequests.get(resolvedKey);
      return {
        loaded: !!result,
        loading: !!pending,
        error: detailErrors.get(resolvedKey) || detailErrors.get(key) || null,
        promise: pending || null,
      };
    },
    request: requestMessageDetails,
  };
};

export default function useMessageDetails() {
  const [, setVersion] = useState(0);
  useEffect(() => {
    const listener = () => setVersion((value) => value + 1);
    return subscribeMessageDetails(listener);
  }, []);

  return useMemo(() => {
    const controller = createMessageDetailsController();
    return {
      getMessage: (threadUid, message) => controller.getMessage(threadUid, message),
      getState: (threadUid, message) => controller.getState(threadUid, message),
      request: (threadUid, message, options) => controller.request(threadUid, message, options),
    };
  }, []);
}
